import React from 'react';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import { useStringsContext } from '../../../../contexts/strings';
import { useMatchNotificationConf } from '../../../../hooks/useMatches';
import { useSegments } from '../../../../hooks/useSegments';
import moment from 'moment';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../common/push-notification/push-notification-column';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';

interface Props {
  match: any;
  selectedAbbrv: string;
  leagueAbbrv: string;
  season: string;
}

export default function MatchPushNotificationColumn(props: Props) {
  const { strings } = useStringsContext();
  const { selectedAbbrv, match, leagueAbbrv, season } = props;
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = useMatchNotificationConf({
    abbrv: selectedAbbrv,
    id: match.id,
  });
  const { query: segmentQuery } = useSegments(selectedAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const matchNotificationConf = query.data;
  const pushNotificationId = matchNotificationConf?.id;

  if (isLoading) {
    return <>{strings.pushNotifications.loading}</>;
  }

  const matchId = match.id.toString();
  const leagueName = leagueAbbrv;

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <PushNotificationColumn
        pushNotification={matchNotificationConf}
        onClick={() => {
          setOpenPushNotificationPopup(true);
        }}
        abbrv={selectedAbbrv}
      />

      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={selectedAbbrv}
          itemInfo={
            <Box>
              <Alert icon={false} severity="info" color="info" variant="standard">
                <Typography variant="h6">
                  {match.homeTeamName} - {match.visitingTeamName}
                </Typography>
                {strings.matches.round} {match.round} | {match.startDate}
              </Alert>
              {pushNotificationId ? (
                <>
                  <Box sx={{ mt: 5, textAlign: 'right' }}>
                    <Button
                      component={Link}
                      to={`/pushdashboard/${selectedAbbrv}?match=${matchId}`}
                      variant="outlined"
                      startIcon={<AddIcon />}
                    >
                      {strings.pushNotifications.createPushNotificationTitle}
                    </Button>
                  </Box>
                  <Divider />
                </>
              ) : null}
            </Box>
          }
          content={{
            target: matchNotificationConf?.notification?.target,
            scheduledTime: matchNotificationConf?.scheduledTime
              ? moment(matchNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : moment(match.startDate, 'YYYY-MM-DD HH:mm').isSameOrAfter(moment().add(1, 'hour'))
                ? moment(match.startDate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD[T]HH:mm')
                : undefined,
            title: matchNotificationConf?.notification?.title || '',
            description: matchNotificationConf?.notification?.description || '',
          }}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={matchNotificationConf?.status === 'SENT'}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                matchId,
                season,
                leagueName,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                matchId,
                season,
                leagueName,
                target,
              },
              createdAt: (matchNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              matchId: match.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => {
            setOpenPushNotificationPopup(false);
          }}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
